import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '../../../../lib/utils'

export type StatusTone = 'error' | 'warning' | 'info'

type ExpandableErrorMessageProps = {
  message: string | undefined
  className?: string
  testId?: string
  title?: string
  /** Severity accent for the hover ring and the popup's leading edge. The text
   *  itself stays neutral: severity is carried by colour accents and icons, not
   *  by painting the whole message red. */
  tone?: StatusTone
}

const POPUP_ACCENT_CLASS: Record<StatusTone, string> = {
  error: 'border-l-destructive',
  warning: 'border-l-warning',
  info: 'border-l-info',
}

// A generation error can be rendered in both the node footer and an open
// configuration panel. Announce identical text once so screen-reader users do
// not hear the same assertive alert twice.
const liveAnnouncementOwners = new Map<string, string>()

// The popup has a fixed on-screen width so a long provider error reads the same
// regardless of how narrow the node is or how far the canvas is zoomed.
const POPUP_WIDTH = 320
const POPUP_MAX_HEIGHT = 240
const POPUP_GAP = 6
const VIEWPORT_MARGIN = 8

type PopupPosition = {
  left: number
  top: number
  width: number
  maxHeight: number
  placement: 'below' | 'above'
}

function computePopupPosition(anchor: DOMRect): PopupPosition {
  const width = Math.min(POPUP_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2)
  const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN
  const centeredLeft = anchor.left + anchor.width / 2 - width / 2
  const left = Math.max(VIEWPORT_MARGIN, Math.min(centeredLeft, maxLeft))
  const spaceBelow = window.innerHeight - anchor.bottom - POPUP_GAP - VIEWPORT_MARGIN
  const spaceAbove = anchor.top - POPUP_GAP - VIEWPORT_MARGIN
  if (spaceBelow >= POPUP_MAX_HEIGHT || spaceBelow >= spaceAbove) {
    const maxHeight = Math.min(POPUP_MAX_HEIGHT, spaceBelow)
    return { left, top: anchor.bottom + POPUP_GAP, width, maxHeight, placement: 'below' }
  }
  const maxHeight = Math.min(POPUP_MAX_HEIGHT, spaceAbove)
  return { left, top: anchor.top - POPUP_GAP, width, maxHeight, placement: 'above' }
}

/**
 * Keeps long generation errors compact until the user asks for the full text.
 * The full text opens in a fixed-width popup portaled to the document body, so
 * it can extend beyond the node instead of stretching the node's layout. The
 * popup closes on the next pointer interaction outside the message or popup.
 */
export function ExpandableErrorMessage({
  message,
  className,
  testId,
  title,
  tone = 'error',
}: ExpandableErrorMessageProps) {
  const [expanded, setExpanded] = useState(false)
  const [liveAnnouncement, setLiveAnnouncement] = useState<string>()
  const [position, setPosition] = useState<PopupPosition>()
  // Keyboard activation moves focus into the popup so it can be scrolled.
  const [focusPopupOnOpen, setFocusPopupOnOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const announcementOwnerId = useId()
  const popupId = useId()

  useEffect(() => {
    if (!message || liveAnnouncementOwners.has(message)) {
      setLiveAnnouncement(undefined)
      return
    }

    liveAnnouncementOwners.set(message, announcementOwnerId)
    if (buttonRef.current !== document.activeElement) {
      setLiveAnnouncement(message)
    }

    return () => {
      if (liveAnnouncementOwners.get(message) === announcementOwnerId) {
        liveAnnouncementOwners.delete(message)
      }
    }
  }, [announcementOwnerId, message])

  useEffect(() => {
    if (!expanded) return

    const collapseOnOutsidePointer = (event: PointerEvent) => {
      const path = event.composedPath()
      const button = buttonRef.current
      const popup = popupRef.current
      if (button && !path.includes(button) && !(popup && path.includes(popup))) {
        setExpanded(false)
      }
    }
    const collapseOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setExpanded(false)
        buttonRef.current?.focus()
      }
    }

    document.addEventListener('pointerdown', collapseOnOutsidePointer, true)
    document.addEventListener('keydown', collapseOnEscape)
    return () => {
      document.removeEventListener('pointerdown', collapseOnOutsidePointer, true)
      document.removeEventListener('keydown', collapseOnEscape)
    }
  }, [expanded])

  // Follow the anchor while open: the canvas can pan/zoom under the popup, and
  // the node can move, without any event reaching this component.
  useLayoutEffect(() => {
    if (!expanded) {
      setPosition(undefined)
      return
    }

    let frame = 0
    let last: PopupPosition | undefined
    const track = () => {
      const button = buttonRef.current
      if (button) {
        const next = computePopupPosition(button.getBoundingClientRect())
        if (
          !last ||
          last.left !== next.left ||
          last.top !== next.top ||
          last.width !== next.width ||
          last.maxHeight !== next.maxHeight ||
          last.placement !== next.placement
        ) {
          last = next
          setPosition(next)
        }
      }
      frame = requestAnimationFrame(track)
    }
    track()
    return () => cancelAnimationFrame(frame)
  }, [expanded])

  useEffect(() => {
    if (expanded && position && focusPopupOnOpen) {
      popupRef.current?.focus()
      setFocusPopupOnOpen(false)
    }
  }, [expanded, position, focusPopupOnOpen])

  if (!message) return null

  return (
    <Fragment>
      {liveAnnouncement === message && (
        <span className="sr-only" role="alert" aria-live="assertive" aria-atomic="true">
          {message}
        </span>
      )}
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={expanded}
        aria-controls={expanded ? popupId : undefined}
        className={cn(
          'nodrag nowheel block line-clamp-2 w-full cursor-pointer rounded-sm text-left transition-colors duration-150 hover:bg-white/10 hover:ring-1 hover:ring-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
          expanded && 'bg-white/10 ring-1 ring-white/20',
          className,
        )}
        data-testid={testId}
        title={expanded ? undefined : (title ?? message)}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation()
          // detail === 0 means the click came from the keyboard (Enter/Space).
          if (!expanded && event.detail === 0) setFocusPopupOnOpen(true)
          setExpanded(true)
        }}
      >
        {message}
      </button>
      {expanded &&
        createPortal(
          <div
            ref={popupRef}
            id={popupId}
            role="dialog"
            tabIndex={-1}
            aria-label={title ?? message}
            data-testid={testId ? `${testId}-popup` : undefined}
            data-placement={position?.placement}
            data-tone={tone}
            className={cn(
              'nodrag nowheel nopan fixed z-[1050] overflow-y-auto whitespace-pre-wrap outline-none focus-visible:ring-2 focus-visible:ring-primary/50 break-words rounded-md border border-l-2 border-border bg-popover px-3 py-2 text-left text-sm text-foreground/90 shadow-lg select-text',
              POPUP_ACCENT_CLASS[tone],
            )}
            style={{
              width: position?.width ?? POPUP_WIDTH,
              maxHeight: position?.maxHeight ?? POPUP_MAX_HEIGHT,
              left: position?.left ?? 0,
              top: position?.top ?? 0,
              transform: position?.placement === 'above' ? 'translateY(-100%)' : undefined,
              visibility: position ? 'visible' : 'hidden',
            }}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            {message}
          </div>,
          document.body,
        )}
    </Fragment>
  )
}
